import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
// Redux store
import { useSelector, useDispatch } from 'react-redux';
import { getVideoInfo } from '../../features/video/videoSlice';
// Components
import { TranscriptContainer, CustomVideo } from '../../components/videos';
// HTTP client
import axios from 'axios';
// Spinner
import { BounceLoader } from 'react-spinners';

const Share = () => {
  const dispatch = useDispatch();
  const { clipId, state } = useParams();

  const [clipInfo, setClipInfo] = useState();
  const [loading, setLoading] = useState(false);
  const { video, isLoading, isError } = useSelector((s) => s.video);
  // transcript will always be nested: array of paragraphs
  const [transcript, setTranscript] = useState([]);

  // 1) Fetch clip metadata and then the video info
  useEffect(() => {
    const getClipInfo = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`/api/${state}/video/clip/${clipId}/`);
        setClipInfo(data);
        if (data.VideoId) {
          dispatch(getVideoInfo({ videoId: data.VideoId, state }));
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    getClipInfo();
  }, [clipId, state, dispatch]);

  // 2) When both clipInfo and video.transcript are ready, build our nested transcript slice
  useEffect(() => {
    if (!clipInfo || !video.transcript) return;

    // Detect flat vs nested transcript
    const isFlat =
      Array.isArray(video.transcript) && video.transcript.length > 0 && video.transcript[0].hasOwnProperty('text');

    if (isFlat) {
      // FLAT: filter chunks by time overlap with the clip window
      const startSec = parseFloat(clipInfo.StartTime);
      const endSec = parseFloat(clipInfo.EndTime);
      const filtered = video.transcript.filter((chunk) => chunk.endTime >= startSec && chunk.startTime <= endSec);
      setTranscript(filtered.map((chunk) => chunk));
    } else {
      // NESTED: use StartPosition/EndPosition to slice by paragraph & word indexes
      const [startPara, startWord] = clipInfo.StartPosition.split('-').map((n) => parseInt(n, 10));
      const [endPara, endWord] = clipInfo.EndPosition.split('-').map((n) => parseInt(n, 10));

      const sliceParas = video.transcript.slice(startPara, endPara + 1);
      const subTranscript = sliceParas.map((para, idx) => {
        if (sliceParas.length === 1) {
          return para.slice(startWord, endWord + 1);
        }
        if (idx === 0) {
          return para.slice(startWord);
        }
        if (idx === sliceParas.length - 1) {
          return para.slice(0, endWord + 1);
        }
        return para;
      });
      setTranscript(subTranscript);
    }
  }, [clipInfo, video.transcript]);

  if (isLoading || loading) {
    return (
      <div className="flex justify-center items-center w-full h-screen">
        <BounceLoader color="#1DB0FF" size={150} />
      </div>
    );
  }

  if (isError) return <div>Error loading video</div>;

  return (
    <div className="w-full flex gap-5">
      {video && clipInfo && (
        <>
          {/* Left panel: video clip and metadata */}
          <div className="w-[40%] h-full overflow-hidden">
            <div className="py-[15px] px-5 border border-[#E5E7EB] bg-[#F9FAFB] mb-4">
              <div className="border-b border-[#E5E7EB] mb-5 pb-2.5">
                <span className="font-playfair text-[28px] capitalize text-darkblue font-medium leading-8">
                  Clip: <strong>{clipInfo.Title}</strong>
                </span>
              </div>
              {clipInfo.Description && (
                <div className="font-playfair text-lg font-medium text-darkblue mb-4">{clipInfo.Description}</div>
              )}
              <CustomVideo start={clipInfo.StartTime} end={clipInfo.EndTime} url={video.Link} />
            </div>
          </div>

          {/* Right panel: transcript preview (nested) */}
          {clipInfo.IncludeTranscript && <TranscriptContainer transcript={transcript} miniTranscript />}
        </>
      )}
    </div>
  );
};

export default Share;
